import { getUserDetail, getUserDetailAccount } from "api/userAPI";
import { CustomError } from "api/interfaces/commonInterface.interface";
import { datadogRum } from "@datadog/browser-rum";
import {
  challengeProps,
  LoginResponse,
  registerCredentialOption,
  SIGNUP_STEP,
  UseKeyType,
  verifyCredentialOption,
} from "api/interfaces/authInterface.interface";
import {
  ACCESS_TOKEN,
  AxiosInstance,
  DEVICE_ID,
  REFRESH_TOKEN,
} from "api/axiosInstance";
import { getCookie, setCookie } from "cookies/cookie";
import { getCookieExpireTime } from "./timeUtil";
import { UserInfo } from "api/interfaces/userInterface.interface";
import { Option } from "./options";
import {
  checkEmpty,
  encodeArrayBuffer,
  encodeArrayBufferToString,
  encodeStringToUint8Array,
} from "./functions";
import { onChangeRead } from "redux/reducers/announcement/announcementSlice";
import { store } from "redux/store";
import { postAccountAccessCheck } from "api/accountAPI";

export const susccessSteps = [
  SIGNUP_STEP.SUCCESS,
  SIGNUP_STEP.TRIAL_SUCCESS,
  SIGNUP_STEP.RESELLER_SIGNUP_SUCCESS,
  SIGNUP_STEP.RESELLER_SUBMISSION_SUCCESS,
  SIGNUP_STEP.RESET_PASSWORD_SUCCESS,
  SIGNUP_STEP.TRIAL_SEND_EMAIL,
];
export const successMessages = {
  title: {
    [SIGNUP_STEP.SUCCESS]: "Successfully invited to the organization.",
    [SIGNUP_STEP.TRIAL_SUCCESS]:
      "You now have 60 days of free \naccess to the service!",
    [SIGNUP_STEP.RESELLER_SIGNUP_SUCCESS]: "Congratulations",
    [SIGNUP_STEP.RESELLER_SUBMISSION_SUCCESS]: "Submission Successful",
    [SIGNUP_STEP.RESET_PASSWORD_SUCCESS]: "Password Reset Successful",
    [SIGNUP_STEP.TRIAL_SEND_EMAIL]: "Verify your email address",
  },
  description: {
    [SIGNUP_STEP.SUCCESS]: "You may now login and use myDW.",
    [SIGNUP_STEP.TRIAL_SUCCESS]:
      "Our DW Sales Team will reach out to help you make the most of your trial and offer personalized support tailored to your needs, so you can continue using the service with confidence.\nIf you have any questions, feel free to contact us anytime.",
    [SIGNUP_STEP.RESELLER_SIGNUP_SUCCESS]:
      "Your account has been successfully created. You may now login and use myDW.",
    [SIGNUP_STEP.RESELLER_SUBMISSION_SUCCESS]:
      "Thank you for submitting your application \nto the Digital Watchdog.\nA sales expert will contact you within 24 hours.",
    [SIGNUP_STEP.RESET_PASSWORD_SUCCESS]:
      "Your password has been reset. \nYou can now login and use myDW.",
    [SIGNUP_STEP.TRIAL_SEND_EMAIL]:
      "Click on the link to complete the verification process.\nYou might need to check your spam folder.",
  },
};

const orgDisableError: CustomError = {
  message: "The Organization has been disabled",
  name: "OrgDisableError",
  response: {
    data: {
      error: 1000,
      errorString: "The Organization has been disabled",
      result: null,
    },
    status: 500,
    headers: "application/json",
  },
};

const permissionDeniedError: CustomError = {
  message: "Permission denied",
  name: "PermissionDeniedError",
  response: {
    data: {
      error: 1000,
      errorString: "Permission denied",
      result: null,
    },
    status: 500,
    headers: "application/json",
  },
};

export const setUserInfo = (
  auth: any,
  accountId?: string,
  toSearchParam?: URLSearchParams
) => {
  sessionStorage.clear();
  if (!accountId && toSearchParam && toSearchParam.get("accountId")) {
    return getUserDetailAccount(toSearchParam.get("accountId") as string).then(
      (response) => {
        auth.login(response, false);
        return response;
      }
    );
  }

  if (!!accountId) {
    return getUserDetailAccount(accountId).then((response) => {
      auth.login(response, false);
      return response;
    });
  }

  return getUserDetail().then((response) => {
    auth.login(response, false);
    return response;
  });
};

export const setLoginToken = ({
  accessToken,
  refreshToken,
  userKey,
  email,
  memberKey,
  hiddenKey,
  useKeyType = "member",
}: {
  accessToken: string;
  refreshToken: string;
  userKey: string;
  email: string;
  memberKey: string;
  hiddenKey: string;
  useKeyType?: UseKeyType;
}) => {
  datadogRum.setUser({
    email,
  });
  setCookie({
    name: ACCESS_TOKEN,
    value: accessToken,
    options: {
      path: "/",
      expires: getCookieExpireTime(6),
    },
  });
  setCookie({
    name: REFRESH_TOKEN,
    value: refreshToken,
    options: {
      path: "/",
      expires: getCookieExpireTime(72),
    },
  });
  setAxiosHeaders({
    accessToken: accessToken,
    deviceId: userKey,
    roleId: useKeyType === "member" ? memberKey : hiddenKey,
  });
  localStorage.setItem("r-mem", memberKey);
  localStorage.setItem("r-hid", hiddenKey);
  if (userKey) {
    localStorage.setItem(DEVICE_ID, userKey);
  }
};

export const getUseRoleType = async (
  accountId: string,
  email: string
): Promise<UseKeyType | null> => {
  let useKeyType: UseKeyType = "member";
  try {
    const keyType = await postAccountAccessCheck({
      accountId: accountId,
      email: email,
    });
    if (!keyType?.result?.hidden && !keyType?.result?.member) {
      return null;
    }
    if (keyType?.result?.hidden && !keyType?.result?.member) {
      useKeyType = "hidden";
    }
    return useKeyType;
  } catch (error) {
    return null;
  }
};

export const onSuccessLogin = async (
  auth: any,
  email: string,
  loginData: LoginResponse,
  onError: (err?: CustomError) => void,
  toSearchParam?: URLSearchParams
) => {
  store.dispatch(onChangeRead(false));
  setAxiosHeaders({
    accessToken: loginData.accessToken,
    deviceId: loginData.userKey,
    roleId: loginData.memberKey,
  });
  let accountId = loginData.accountId;
  let checkRoleId = false;
  let roleId: string = loginData.memberKey;
  let useKeyType: UseKeyType = "member";
  if (
    toSearchParam &&
    toSearchParam.get("accountId") &&
    toSearchParam.get("accountId") !== "undefined"
  ) {
    accountId = toSearchParam.get("accountId") as string;
    checkRoleId = true;
  }
  if (
    toSearchParam &&
    toSearchParam.get("account") &&
    toSearchParam.get("account") !== "undefined"
  ) {
    accountId = toSearchParam.get("account") as string;
    checkRoleId = true;
  }
  if (checkRoleId && email) {
    const useKeyTypeResult = await getUseRoleType(accountId, email);
    if (!useKeyTypeResult) {
      onError(permissionDeniedError);
      return;
    }
    useKeyType = useKeyTypeResult;
    roleId =
      useKeyType === "member" ? loginData.memberKey : loginData.hiddenKey;
  }
  // 2fa 필요 없을 때 로그인
  setLoginToken({
    accessToken: loginData.accessToken,
    refreshToken: loginData.refreshToken,
    userKey: loginData.userKey,
    email: email,
    memberKey: loginData.memberKey,
    hiddenKey: loginData.hiddenKey,
    useKeyType: useKeyType,
  });

  setUserInfo(auth, accountId, toSearchParam)
    .then(function (resolvedData) {
      const accountData = resolvedData as UserInfo;
      //BUG [RND-330] email link
      if (accountData !== undefined && accountData.isAccount !== 0) {
        auth.setShowIntercom(true);
        let toSearchParamUrl = toSearchParam?.get("url");
        auth.login(accountData, false);
        if (!!toSearchParamUrl) {
          auth.processLinkNavigate({
            toPathParam: toSearchParamUrl as string,
            toSearchParam: toSearchParam,
            userInfo: accountData,
            roleId: roleId,
            loginAccountId: accountData.accounts.accountId,
          });
        } else {
          auth.loginNavigator("/", accountData, false);
        }
        if (window.dataLayer) {
          window.dataLayer.push({
            event: "dw_login",
            user_id: accountData.userId,
            user_role: accountData.accounts.accountLevel,
            organization_id: accountData.accounts.accountId,
            login_time: new Date().toISOString(),
          });
        }
      } else {
        throw new Error("The Organization has been disabled");
      }
    })
    .catch(function (err) {
      if (err.message === "The Organization has been disabled") {
        onError(orgDisableError);
        return;
      }
      onError(err as CustomError);
    });
};

export const getSelectedValue = (value: string, options: Option[]) => {
  if (checkEmpty(value)) return undefined;
  return options.find((option) => option.value === value);
};

export const getPublicKeyCredentialCreationOptions = ({
  email,
  challenge,
  rpName,
  rpId,
  timeout,
}: { email: string } & challengeProps): PublicKeyCredentialCreationOptions => {
  return {
    challenge: encodeArrayBuffer(challenge), // Base64 URL 디코딩 사용
    rp: {
      name: rpName,
      id: rpId,
    },
    user: {
      id: encodeStringToUint8Array(email),
      name: email,
      displayName: email,
    },
    pubKeyCredParams: [
      { alg: -7, type: "public-key" }, // ES256
      { alg: -257, type: "public-key" }, // RS256
      { alg: -8, type: "public-key" }, // EdDSA
    ],
    authenticatorSelection: {
      authenticatorAttachment: "platform" as const,
      residentKey: "preferred" as const,
      userVerification: "required" as const,
    },
    timeout: timeout,
    attestation: "none" as const,
    extensions: { credProps: true } as any,
  };
};

export const createCredential = async (
  publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions
): Promise<registerCredentialOption> => {
  try {
    // WebAuthn 지원 여부 확인
    if (!navigator.credentials || !navigator.credentials.create) {
      throw new Error("1100001");
    }

    const credential = (await navigator.credentials.create({
      publicKey: publicKeyCredentialCreationOptions,
    })) as PublicKeyCredential;

    if (!credential) {
      throw new Error("1100002");
    }

    const response = credential?.response as AuthenticatorAttestationResponse;

    if (!response) {
      throw new Error("1100002");
    }

    // 패스키 등록 API Request Data
    return {
      credentialId: credential.id,
      publicKey: encodeArrayBufferToString(response.attestationObject),
      credential: encodeArrayBufferToString(response.clientDataJSON),
    };
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error?.message);
    }
    throw new Error("1100002");
  }
};

export const verifyCredential = async (
  publicKeyCredentialCreationOptions: PublicKeyCredentialRequestOptions
): Promise<verifyCredentialOption> => {
  try {
    // WebAuthn 지원 여부 확인
    if (!navigator.credentials || !navigator.credentials.get) {
      throw new Error("1100001");
    }
    const credential = (await navigator.credentials.get({
      publicKey: {
        ...publicKeyCredentialCreationOptions,
        userVerification: "required",
      },
    })) as PublicKeyCredential;

    if (!credential) {
      throw new Error("1100002");
    }

    const response = credential.response as AuthenticatorAssertionResponse;

    // 패스키 검증 API Request Data
    return {
      credentialId: credential.id,
      authenticatorData: encodeArrayBufferToString(response.authenticatorData),
      clientDataJSON: encodeArrayBufferToString(response.clientDataJSON),
      signature: encodeArrayBufferToString(response.signature),
    };
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error?.message);
    }
    throw new Error("1100003");
  }
};

export const setAxiosHeaders = (options?: {
  accessToken?: string;
  deviceId?: string;
  roleId?: string;
}) => {
  if (
    options?.accessToken ||
    !AxiosInstance.defaults.headers.common["Authorization"]
  ) {
    const accessToken = options?.accessToken || getCookie(ACCESS_TOKEN);
    if (!accessToken) {
      return;
    }
    AxiosInstance.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${accessToken}`;
  }
  if (
    options?.deviceId ||
    !AxiosInstance.defaults.headers.common["Device-Id"]
  ) {
    const deviceId = options?.deviceId || localStorage.getItem(DEVICE_ID);
    if (!deviceId) {
      return;
    }
    AxiosInstance.defaults.headers.common["Device-Id"] = deviceId;
  }
  const searchParams = new URLSearchParams(window.location.search);
  let roleId = options?.roleId;
  if (!roleId && searchParams.get("r")) {
    if (searchParams.get("r") === localStorage.getItem("r-hid")) {
      roleId = localStorage.getItem("r-hid") as string;
    } else if (searchParams.get("r") === localStorage.getItem("r-mem")) {
      roleId = localStorage.getItem("r-mem") as string;
    }
  }
  if (!!roleId && roleId !== AxiosInstance.defaults.headers.common["Role-Id"]) {
    AxiosInstance.defaults.headers.common["Role-Id"] = roleId as string;
  }
};
export const clearAxiosHeaders = () => {
  delete AxiosInstance.defaults.headers.common["Authorization"];
  delete AxiosInstance.defaults.headers.common["Device-Id"];
  delete AxiosInstance.defaults.headers.common["Role-Id"];
};
